import { Grid } from '@material-ui/core';
import { EntityConsumedApisCard, EntityProvidedApisCard } from '@backstage/plugin-api-docs';
import {
  EntityAboutCard,
  EntityDependsOnComponentsCard,
  EntityDependsOnResourcesCard,
  EntityLayout,
  EntityLinksCard,
  EntitySwitch,
  isComponentType,
} from '@backstage/plugin-catalog';
import { EntityCatalogGraphCard } from '@backstage/plugin-catalog-graph';
import {
  EntityGithubActionsContent,
  isGithubActionsAvailable,
} from '@backstage-community/plugin-github-actions';
import { EmptyState } from '@backstage/core-components';
import { EntityGithubPullRequestsContent } from '@roadiehq/backstage-plugin-github-pull-requests';
import { EntityGithubInsightsContributorsCard } from '@roadiehq/backstage-plugin-github-insights';
import { EntityCodespacesCard } from '@internal/backstage-plugin-github-codespaces';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { githubViewPermission } from '@internal/backstage-plugin-github-common';
import { RadixCard } from '@internal/backstage-plugin-radix';
import { AzureCard } from '@internal/backstage-plugin-azure';

import { EntityMarkdownCard } from '../EntityMarkdown/EntityMarkdownCard';
import { DefaultEntityPage } from './DefaultEntityPage';
import { EntityWarningContent } from './EntityWarningContent';
import { techdocsContent } from './TechdocsContent';

const isNotComponentType = (types: string | string[]) => {
  const typeArray = Array.isArray(types) ? types : [types];
  return (entity: any) => {
    return !typeArray.some(type => isComponentType(type)(entity));
  };
};

const GitHubPermissionDeniedMessage = (
  <EmptyState
    missing="info"
    title="Restricted"
    description="You need GitHub access to view this content."
  />
);

const cicdContent = (
  <EntitySwitch>
    <EntitySwitch.Case if={isGithubActionsAvailable}>
      <EntityGithubActionsContent />
    </EntitySwitch.Case>
    <EntitySwitch.Case>
      <EmptyState title="No CI/CD available for this entity" missing="info" />
    </EntitySwitch.Case>
  </EntitySwitch>
);

const OverviewContent = () => (
  <Grid container spacing={3} alignItems="stretch">
    <EntityWarningContent />
    <Grid item md={6} xs={12}>
      <Grid container>
        <Grid item xs={12}>
          <EntityAboutCard />
        </Grid>
        <Grid item xs={12}>
          <EntityCodespacesCard />
        </Grid>
        <Grid item xs={12}>
          <EntityLinksCard />
        </Grid>
        <Grid item xs={12}>
          <RadixCard />
        </Grid>
        <Grid item xs={12}>
          <AzureCard />
        </Grid>
        <Grid item xs={12}>
          <EntityMarkdownCard />
        </Grid>
      </Grid>
    </Grid>
    <Grid item md={6} xs={12}>
      <Grid container>
        <Grid item xs={12}>
          <EntityCatalogGraphCard height={200} />
        </Grid>
        <Grid item xs={12}>
          <EntityGithubInsightsContributorsCard />
        </Grid>
      </Grid>
    </Grid>
  </Grid>
);

const serviceEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <OverviewContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/pull-requests" title="Pull Requests">
      <RequirePermission
        permission={githubViewPermission}
        errorPage={GitHubPermissionDeniedMessage}
      >
        <EntityGithubPullRequestsContent />
      </RequirePermission>
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      <RequirePermission
        permission={githubViewPermission}
        errorPage={GitHubPermissionDeniedMessage}
      >
        {cicdContent}
      </RequirePermission>
    </EntityLayout.Route>

    <EntityLayout.Route path="/api" title="API">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6} xs={12}>
          <Grid container>
            <Grid item xs={12}>
              <EntityProvidedApisCard />
            </Grid>
          </Grid>
        </Grid>
        <Grid item md={6} xs={12}>
          <Grid container>
            <Grid item xs={12}>
              <EntityConsumedApisCard />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6} xs={12}>
          <Grid container>
            <Grid item xs={12}>
              <EntityDependsOnComponentsCard />
            </Grid>
          </Grid>
        </Grid>
        <Grid item md={6} xs={12}>
          <Grid container>
            <Grid item xs={12}>
              <EntityDependsOnResourcesCard />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>

    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

const websiteEntityPage = (
  <EntityLayout>
    <EntityLayout.Route path="/" title="Overview">
      <OverviewContent />
    </EntityLayout.Route>

    <EntityLayout.Route path="/pull-requests" title="Pull Requests">
      <RequirePermission
        permission={githubViewPermission}
        errorPage={GitHubPermissionDeniedMessage}
      >
        <EntityGithubPullRequestsContent />
      </RequirePermission>
    </EntityLayout.Route>

    <EntityLayout.Route path="/ci-cd" title="CI/CD">
      <RequirePermission
        permission={githubViewPermission}
        errorPage={GitHubPermissionDeniedMessage}
      >
        {cicdContent}
      </RequirePermission>
    </EntityLayout.Route>

    <EntityLayout.Route path="/dependencies" title="Dependencies">
      <Grid container spacing={3} alignItems="stretch">
        <Grid item md={6} xs={12}>
          <Grid container>
            <Grid item xs={12}>
              <EntityDependsOnComponentsCard />
            </Grid>
          </Grid>
        </Grid>
        <Grid item md={6} xs={12}>
          <Grid container>
            <Grid item xs={12}>
              <EntityDependsOnResourcesCard />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </EntityLayout.Route>
    <EntityLayout.Route path="/docs" title="Docs">
      {techdocsContent}
    </EntityLayout.Route>
  </EntityLayout>
);

export const ComponentEntityPage = (
  <EntitySwitch>
    <EntitySwitch.Case if={isComponentType('service')}>{serviceEntityPage}</EntitySwitch.Case>
    <EntitySwitch.Case if={isNotComponentType('documentation')}>
      {websiteEntityPage}
    </EntitySwitch.Case>

    <EntitySwitch.Case>{DefaultEntityPage}</EntitySwitch.Case>
  </EntitySwitch>
);
